import React from 'react'
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export type SegmentTabBarVariant = 'pill' | 'underline'

export interface SegmentTabItem {
  label: string
  value: string
  /** Optional count badge (e.g. pending invites) */
  badge?: number
}

export interface SegmentTabBarProps {
  tabs: SegmentTabItem[]
  value: string
  onChange: (value: string) => void
  variant?: SegmentTabBarVariant
  /** Accessible name for the tab list */
  ariaLabel: string
  sx?: SxProps<Theme>
}

const badgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 22,
  height: 18,
  px: 0.75,
  borderRadius: 999,
  backgroundColor: INTERAX_COLORS.white10,
  color: INTERAX_COLORS.white70,
  fontSize: 11,
  fontWeight: 600
} as const

function TabLabel({ label, badge }: { label: string; badge?: number }) {
  if (badge == null) {
    return <span>{label}</span>
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <span>{label}</span>
      <Box component="span" sx={badgeSx}>
        {badge}
      </Box>
    </Stack>
  )
}

/**
 * Segmented tab bar for switching between related panels (pill or underline).
 */
export const SegmentTabBar: React.FC<SegmentTabBarProps> = ({
  tabs,
  value,
  onChange,
  variant = 'pill',
  ariaLabel,
  sx
}) => {
  if (!tabs.length) return null

  const handleChange = (_event: React.SyntheticEvent, newValue: string | number) => {
    onChange(String(newValue))
  }

  if (variant === 'underline') {
    return (
      <Box sx={{ borderBottom: `1px solid ${INTERAX_COLORS.white10}`, ...sx }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={ariaLabel}
          TabIndicatorProps={{
            sx: { backgroundColor: INTERAX_COLORS.sun, height: 3, borderRadius: '3px 3px 0 0' }
          }}
          sx={{
            minHeight: { xs: 44, sm: 44, md: 44 },
            '& .MuiTab-root': {
              color: INTERAX_COLORS.white70,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: 14, sm: 14, md: 15 },
              minHeight: { xs: 44, sm: 44, md: 44 },
              minWidth: { xs: 96, sm: 120, md: 140 },
              '&.Mui-selected': { color: INTERAX_COLORS.white }
            }
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={<TabLabel label={tab.label} badge={tab.badge} />} />
          ))}
        </Tabs>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        p: { xs: 0.5, sm: 0.5, md: 0.5 },
        gap: { xs: 2, sm: 3, md: 3 },
        width: '100%',
        background: INTERAX_COLORS.offBlack,
        boxShadow: '2px 2px 2px rgba(255, 255, 255, 0.1), inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
        borderRadius: '10px',
        ...sx
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        aria-label={ariaLabel}
        sx={{
          width: '100%',
          minHeight: { xs: 40, sm: 40, md: 40 },
          '& .MuiTabs-flexContainer': {
            gap: { xs: 2, sm: 3, md: 3 },
            width: '100%'
          },
          '& .MuiTabs-indicator': { display: 'none' }
        }}
      >
        {tabs.map((tab) => {
          const isActive = value === tab.value
          return (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Typography
                  component="span"
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    lineHeight: 1.5,
                    color: 'inherit',
                    textTransform: 'none'
                  }}
                >
                  <TabLabel label={tab.label} badge={tab.badge} />
                </Typography>
              }
              sx={{
                boxSizing: 'border-box',
                minHeight: { xs: 40, sm: 40, md: 40 },
                flex: 1,
                borderRadius: '10px',
                textTransform: 'none',
                color: INTERAX_COLORS.white,
                ...(isActive && {
                  background: INTERAX_COLORS.white10,
                  boxShadow: `0px 5px 10px rgba(0, 0, 0, 0.25), inset 0px -1px 1px ${INTERAX_COLORS.neutralBorder}`
                }),
                '&.Mui-selected': { color: INTERAX_COLORS.white },
                '&:hover': {
                  ...(!isActive && { background: INTERAX_COLORS.white10 })
                }
              }}
            />
          )
        })}
      </Tabs>
    </Box>
  )
}

export default SegmentTabBar
